import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Printer, MapPin, Phone, Mail, CalendarDays, X as XIcon, ClipboardList, ScanLine, Navigation } from "lucide-react";
import { API } from "@/config";
import { formatSlot } from "@/utils/slotLabel";
import { useAuth } from "@/context/AuthProvider";
import { RunSheet } from "@/pages/admin/RunSheet";
import { DriverScan } from "@/pages/admin/DriverScan";
import { MyRoute } from "@/pages/admin/MyRoute";
import { ConversationPanel } from "@/components/admin/ConversationPanel";
import { LabelPrintOverlay } from "@/components/admin/LabelPrintOverlay";

export const EmployeeApp = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const showRunSheet = location.pathname.endsWith("/run-sheet");
  const showScan = location.pathname.endsWith("/scan");
  const showRoute = location.pathname.endsWith("/my-route");
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setData(null); // clear stale data so UI shows "Loading…" during day-switch
    try {
      const r = await axios.get(`${API}/employee/pick-sheet`, { params: { day } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load pick sheet");
    }
  }, [day]);

  useEffect(() => {
    if (user === false) { nav("/login?role=employee", { replace: true }); return; }
    if (user && user.role && !["employee", "admin"].includes(user.role)) {
      const home = { customer: "/account" }[user.role] || "/";
      nav(home, { replace: true });
      return;
    }
    if (user && (user.role === "employee" || user.role === "admin")) load();
  }, [user, nav, load]);

  const openDetail = async (res) => {
    try {
      const r = await axios.get(`${API}/employee/reservations/${res.id}`);
      setSelected(r.data);
    } catch { toast.error("Failed to load reservation"); }
  };

  const [labelPreview, setLabelPreview] = useState(null);
  const printLabel = async (unit) => {
    try {
      const r = await axios.get(`${API}/employee/inventory/${unit.id}/label`, { responseType: "blob" });
      setLabelPreview({ src: URL.createObjectURL(r.data), title: `${unit.barcode || "Label"}` });
    } catch { toast.error("Failed to load label"); }
  };

  if (!user) return <div className="min-h-screen flex items-center justify-center text-[#B9C8D5]">Loading…</div>;

  return (
    <div className="min-h-screen relative overflow-hidden py-16 px-4">
      <div className="container max-w-6xl relative z-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="eyebrow text-[#FFD166]">Employee Portal</p>
            <h1 className="font-serif text-5xl text-[#F6F1E4] mt-1">Daily Run Sheet</h1>
            <p className="text-[#B9C8D5] mt-2 text-sm">Signed in as {user.email} · {user.role}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => nav(showScan ? "/employee" : "/employee/scan")} className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${showScan ? "border-[#FFD166]/30 text-[#B9C8D5] hover:text-[#F6F1E4]" : "bg-[#FFD166] hover:bg-[#F4A93A] text-[#040A0F] border-[#FFD166]"}`} data-testid="employee-scan-toggle">
              <ScanLine className="w-3.5 h-3.5" />
              {showScan ? "Back to Pick Sheet" : "Scan & Check In"}
            </button>
            <button onClick={() => nav(showRoute ? "/employee" : "/employee/my-route")} className="btn-ghost inline-flex items-center gap-1" data-testid="employee-my-route-toggle">
              <Navigation className="w-3.5 h-3.5" />
              {showRoute ? "Back to Pick Sheet" : "My Route"}
            </button>
            <button onClick={() => nav(showRunSheet ? "/employee" : "/employee/run-sheet")} className="btn-ghost inline-flex items-center gap-1" data-testid="employee-run-sheet-toggle">
              <ClipboardList className="w-3.5 h-3.5" />
              {showRunSheet ? "Back to Pick Sheet" : "Run Sheet"}
            </button>
            {!showRunSheet && !showScan && <input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="picksheet-date" className="bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg" />}
            {!showRunSheet && !showScan && <button onClick={load} className="btn-ghost" data-testid="picksheet-reload">Reload</button>}
            <button onClick={async () => { await logout(); nav("/"); }} className="btn-ghost" data-testid="employee-logout"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>

        {showScan ? (
          <div className="mt-10"><DriverScan /></div>
        ) : showRoute ? (
          <div className="mt-10"><MyRoute /></div>
        ) : showRunSheet ? (
          <div className="mt-10"><RunSheet /></div>
        ) : (!data ? <div className="mt-10 text-[#B9C8D5]">Loading…</div> : (
          <div className="mt-10 grid lg:grid-cols-[1fr_1.4fr] gap-8">
            <div className="glass p-6">
              <p className="eyebrow text-[#FFD166]">Pick list · {day}</p>
              <h2 className="font-serif text-2xl mt-1 text-[#F6F1E4]">What to grab this morning</h2>
              {data.pick_list.length === 0 ? (
                <div className="mt-6 text-[#B9C8D5] text-sm py-8 text-center border border-dashed border-[#FFD166]/20 rounded-xl" data-testid="picksheet-empty">No deliveries scheduled for this day.</div>
              ) : (
                <ul className="mt-5 space-y-2" data-testid="picksheet-pick-list">
                  {data.pick_list.map((li) => (
                    <li key={li.product_id} className="flex items-center justify-between border-b border-[#FFD166]/10 py-2">
                      <span className="text-[#F6F1E4]">{li.name}</span>
                      <span className="text-[#FFD166] font-serif text-xl">× {li.quantity}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-6 pt-6 border-t border-[#FFD166]/15">
                <p className="eyebrow text-[#B9C8D5]">Pickups today</p>
                {data.pickups.length === 0 ? (
                  <p className="text-sm text-[#B9C8D5] mt-2">None</p>
                ) : (
                  <ul className="mt-2 space-y-1 text-sm">
                    {data.pickups.map((r) => (
                      <li key={r.id} className="flex justify-between">
                        <button onClick={() => openDetail(r)} className="text-[#FFD166] hover:underline">#{r.id.slice(0, 8).toUpperCase()}</button>
                        <span className="text-[#B9C8D5]">{r.delivery_resort}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div>
              <p className="eyebrow text-[#FFD166]">Deliveries · {data.deliveries.length}</p>
              <h2 className="font-serif text-2xl mt-1 text-[#F6F1E4] mb-4">Stops for today</h2>
              {data.deliveries.length === 0 ? (
                <div className="text-[#B9C8D5] text-sm py-8 text-center border border-dashed border-[#FFD166]/20 rounded-xl">No deliveries today.</div>
              ) : (
                <div className="space-y-3" data-testid="picksheet-deliveries">
                  {data.deliveries.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => openDetail(r)}
                      data-testid={`delivery-${r.id}`}
                      className="w-full text-left border border-[#FFD166]/20 rounded-xl p-4 hover:bg-[#FFD166]/5 hover:border-[#FFD166]/50 transition-colors"
                    >
                      <div className="flex justify-between items-start gap-3">
                        <div>
                          <div className="font-serif text-xl text-[#F6F1E4]">{r.customer_name}</div>
                          <div className="text-sm text-[#B9C8D5] mt-0.5">{r.delivery_resort}</div>
                          <div className="text-xs text-[#FFD166] mt-1">Window: {formatSlot(r.delivery_slot)}</div>
                        </div>
                        <div className="text-xs text-[#B9C8D5]">#{r.id.slice(0, 8).toUpperCase()}</div>
                      </div>
                      <ul className="mt-2 text-xs text-[#F6F1E4]/85 space-y-0.5">
                        {r.line_items.map((li, i) => (
                          <li key={li.product_id}>× {li.quantity} — {li.name}</li>
                        ))}
                      </ul>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {selected && <ReservationDetailModal detail={selected} onClose={() => setSelected(null)} onPrintLabel={printLabel} />}
      {labelPreview && <LabelPrintOverlay src={labelPreview.src} title={labelPreview.title} onClose={() => { URL.revokeObjectURL(labelPreview.src); setLabelPreview(null); }} />}
    </div>
  );
};

const ReservationDetailModal = ({ detail, onClose, onPrintLabel }) => {
  const r = detail.reservation;
  const [contactOpen, setContactOpen] = useState(false);
  return (
    <div className="fixed inset-0 z-[100] bg-[#040A0F]/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="employee-detail-modal">
      <div className="glass max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#B9C8D5] hover:text-[#FFD166]" aria-label="Close" data-testid="employee-detail-close"><XIcon className="w-5 h-5" /></button>
        <p className="eyebrow text-[#FFD166]">Reservation #{r.id.slice(0, 8).toUpperCase()}</p>
        <h3 className="font-serif text-3xl mt-1 text-[#F6F1E4]">{r.customer_name}</h3>
        <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm text-[#F6F1E4]">
          <div className="col-span-2 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#FFD166]/50 bg-[#FFD166]/10 text-[#FFD166] hover:bg-[#FFD166]/20 text-sm font-medium"
              data-testid="employee-contact-customer"
            >
              <Mail className="w-4 h-4" /> Contact Customer
            </button>
            {r.sms_allowed === false && <span className="text-[10px] text-[#F4A93A]">Email only — no SMS consent</span>}
          </div>
          <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#FFD166]" />{r.delivery_resort}</div>
          <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#FFD166]" />{r.delivery_date} · {formatSlot(r.delivery_slot)}</div>
        </div>
        {r.notes && <div className="mt-4 p-3 rounded-lg border border-[#FFD166]/25 bg-black/30 text-sm text-[#B9C8D5]"><strong className="text-[#FFD166]">Notes:</strong> {r.notes}</div>}
        {contactOpen && <ConversationPanel reservationId={r.id} customerName={r.customer_name} onClose={() => setContactOpen(false)} />}

        <h4 className="mt-6 font-serif text-xl text-[#F6F1E4]">Items & labels</h4>
        <p className="text-xs text-[#B9C8D5] mb-3">Each unit needs a printed label with its barcode. Tap a barcode to print.</p>
        <div className="space-y-4">
          {detail.items_detail.map((it) => (
            <div key={it.product_id} className="border border-[#FFD166]/20 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="font-medium text-[#F6F1E4]">{it.name}</div>
                <div className="text-xs text-[#FFD166]">× {it.quantity}</div>
              </div>
              {it.assigned_units.length > 0 && (
                <>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[#B9C8D5]">Assigned units</div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {it.assigned_units.map((u) => (
                      <button key={u.id} onClick={() => onPrintLabel(u)} className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 flex items-center gap-1" data-testid={`print-assigned-${u.id}`}>
                        <Printer className="w-3 h-3" /> {u.barcode}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {it.available_units.length > 0 && (
                <>
                  <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[#B9C8D5]">Available to grab</div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {it.available_units.map((u) => (
                      <button key={u.id} onClick={() => onPrintLabel(u)} className="text-xs px-3 py-1.5 rounded-full border border-[#FFD166]/30 text-[#FFD166] hover:bg-[#FFD166]/10 flex items-center gap-1" data-testid={`print-available-${u.id}`}>
                        <Printer className="w-3 h-3" /> {u.barcode}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
